import React, { useState, useEffect } from 'react';
import { get } from '../../helpers/apiHelper';
import { FaChartLine, FaTag, FaEye, FaGift } from 'react-icons/fa';
import useAuthStore from '../../store/authStore';

function OfertasMetricsPage() {
    const [metrics, setMetrics] = useState(null);
    const [ofertas, setOfertas] = useState([]);
    const [marcas, setMarcas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMarca, setSelectedMarca] = useState('all');

    const { user } = useAuthStore();
    const userId = user.userId;

    const fetchData = React.useCallback(async () => {
        setLoading(true);
        try {
            // Obtener marcas
            const marcasResponse = await get(`marcas?usuario=${userId}`);
            setMarcas(marcasResponse);

            // Obtener ofertas
            const ofertasResponse = await get('ofertas');
            setOfertas(ofertasResponse);

            // Calcular métricas desde las ofertas obtenidas
            const metricsData = {
                totalOfertas: ofertasResponse.length,
                ofertasActivas: ofertasResponse.filter(o => o.estado === 'ACTIVA').length,
                totalUsos: ofertasResponse.reduce((total, o) => total + (o.usos || 0), 0),
                totalVisualizaciones: ofertasResponse.reduce((total, o) => total + (o.visualizaciones || 0), 0),
                tasaConversion: ofertasResponse.reduce((total, o) => total + (o.usos || 0), 0) /
                    Math.max(ofertasResponse.reduce((total, o) => total + (o.visualizaciones || 0), 0), 1)
            };
            setMetrics(metricsData);
        } catch (error) {
            console.error("Error al obtener datos:", error);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        if (userId) {
            fetchData();
        }
    }, [userId, fetchData]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount || 0);
    };

    const formatPercentage = (value) => {
        return `${(value * 100).toFixed(1)}%`;
    };

    const getTopOfertas = () => {
        return ofertas
            .sort((a, b) => (b.usos || 0) - (a.usos || 0))
            .slice(0, 5);
    };

    const getOfertasByEstado = () => {
        const estados = {};
        ofertas.forEach(oferta => {
            estados[oferta.estado] = (estados[oferta.estado] || 0) + 1;
        });
        return estados;
    };

    const getOfertasByTipo = () => {
        const tipos = {};
        ofertas.forEach(oferta => {
            tipos[oferta.tipo] = (tipos[oferta.tipo] || 0) + 1;
        });
        return tipos;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando métricas...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">
                        Métricas de Ofertas
                    </h1>
                    <p className="text-gray-600">
                        Analiza el rendimiento de tus ofertas promocionales
                    </p>
                </div>

                {/* Filtros */}
                <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Marca
                            </label>
                            <select
                                value={selectedMarca}
                                onChange={(e) => setSelectedMarca(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="all">Todas las marcas</option>
                                {marcas.map(marca => (
                                    <option key={marca._id} value={marca._id}>
                                        {marca.nombre || marca.displayName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-end">
                            <button
                                onClick={fetchData}
                                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Actualizar
                            </button>
                        </div>
                    </div>
                </div>

                {/* Métricas principales */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg shadow-md">
                        <div className="flex items-center">
                            <FaTag className="text-blue-500 text-2xl mr-3" />
                            <div>
                                <p className="text-sm text-gray-600">Total Ofertas</p>
                                <p className="text-2xl font-bold text-gray-800">{ofertas.length}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-md">
                        <div className="flex items-center">
                            <FaGift className="text-green-500 text-2xl mr-3" />
                            <div>
                                <p className="text-sm text-gray-600">Total Usos</p>
                                <p className="text-2xl font-bold text-gray-800">
                                    {ofertas.reduce((total, oferta) => total + (oferta.usos || 0), 0)}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-md">
                        <div className="flex items-center">
                            <FaEye className="text-purple-500 text-2xl mr-3" />
                            <div>
                                <p className="text-sm text-gray-600">Total Visualizaciones</p>
                                <p className="text-2xl font-bold text-gray-800">
                                    {ofertas.reduce((total, oferta) => total + (oferta.visualizaciones || 0), 0)}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-md">
                        <div className="flex items-center">
                            <FaChartLine className="text-orange-500 text-2xl mr-3" />
                            <div>
                                <p className="text-sm text-gray-600">Tasa Conversión</p>
                                <p className="text-2xl font-bold text-gray-800">
                                    {(() => {
                                        const totalVisualizaciones = ofertas.reduce((total, oferta) => total + (oferta.visualizaciones || 0), 0);
                                        const totalUsos = ofertas.reduce((total, oferta) => total + (oferta.usos || 0), 0);
                                        return totalVisualizaciones > 0 ? formatPercentage(totalUsos / totalVisualizaciones) : '0%';
                                    })()}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Métricas detalladas */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Top 5 Ofertas */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Top 5 Ofertas por Uso</h3>
                        <div className="space-y-3">
                            {getTopOfertas().map((oferta, index) => (
                                <div key={oferta._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center">
                                        <span className="text-sm font-medium text-gray-600 mr-3">#{index + 1}</span>
                                        <div>
                                            <p className="font-medium text-gray-800">{oferta.nombre}</p>
                                            <p className="text-sm text-gray-600">{oferta.tipo}</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-semibold text-gray-800">{oferta.usos || 0} usos</p>
                                        <p className="text-sm text-gray-600">
                                            {oferta.visualizaciones > 0 ? formatPercentage((oferta.usos || 0) / oferta.visualizaciones) : '0%'} conv.
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Distribución por Estado */}
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Distribución por Estado</h3>
                        <div className="space-y-3">
                            {Object.entries(getOfertasByEstado()).map(([estado, count]) => (
                                <div key={estado} className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700 capitalize">
                                        {estado.toLowerCase().replace('_', ' ')}
                                    </span>
                                    <div className="flex items-center">
                                        <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                                            <div
                                                className="bg-blue-500 h-2 rounded-full"
                                                style={{ width: `${(count / ofertas.length) * 100}%` }}
                                            ></div>
                                        </div>
                                        <span className="text-sm font-semibold text-gray-800">{count}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Distribución por Tipo */}
                <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Distribución por Tipo de Oferta</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {Object.entries(getOfertasByTipo()).map(([tipo, count]) => (
                            <div key={tipo} className="bg-gray-50 p-4 rounded-lg">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-medium text-gray-800">
                                            {tipo.replace(/_/g, ' ')}
                                        </p>
                                        <p className="text-sm text-gray-600">{count} ofertas</p>
                                    </div>
                                    <div className="text-2xl font-bold text-blue-600">
                                        {((count / ofertas.length) * 100).toFixed(1)}%
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Métricas de rendimiento */}
                {metrics && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Rendimiento Financiero</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Ventas generadas:</span>
                                    <span className="font-semibold">{formatCurrency(metrics.ventasGeneradas)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Descuentos aplicados:</span>
                                    <span className="font-semibold">{formatCurrency(metrics.descuentosAplicados)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">ROI promedio:</span>
                                    <span className="font-semibold">{formatPercentage(metrics.roiPromedio)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Engagement</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Tasa de apertura:</span>
                                    <span className="font-semibold">{formatPercentage(metrics.tasaApertura)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Tasa de clics:</span>
                                    <span className="font-semibold">{formatPercentage(metrics.tasaClics)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Tiempo promedio:</span>
                                    <span className="font-semibold">{metrics.tiempoPromedio || 'N/A'}</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Clientes</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Clientes nuevos:</span>
                                    <span className="font-semibold">{metrics.clientesNuevos || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Clientes recurrentes:</span>
                                    <span className="font-semibold">{metrics.clientesRecurrentes || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Ticket promedio:</span>
                                    <span className="font-semibold">{formatCurrency(metrics.ticketPromedio)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default OfertasMetricsPage; 